import { useCallback, useEffect } from 'react';
import { takeUntil } from 'rxjs';
import { trpc } from '@client-trpc';
import { filterWidgetEvent } from '@client/foundation/shared/utils/filter-widget-event';
import { useNotifications } from '@client/foundation/notifications/notifications.hook';
import { useChartState } from '@client/state/chart-state.hook';
import { useTradingWorkspaceDestroy$ } from '@client/features/trading-workspace/trading-workspace-scope.context';
import { useWidget } from '@client/widgets/widget.hook';
import type { WidgetPayload } from '@client/widgets/widget.types';
import { useWindow } from '@client/window/window.hook';
import { useCreateWorkspaceLayout } from './create-workspace-layout.hook';
import { useObservable } from '@client/foundation/shared/utils/observable.hook';
import { buildWorkspaceLayoutSnapshot } from './workspace-layout-snapshot.utils';
import { resolveLayoutStatesToFormState } from '@client/features/trading-layouts/layout-template-state.utils';

export const useManageWorkspaceLayouts = () => {
  const { openWidget$ } = useWidget();
  const { openWindow, closeWindow } = useWindow();
  const { notify } = useNotifications();
  const { dispatch, getState$ } = useChartState();
  const workspaceDestroy$ = useTradingWorkspaceDestroy$();
  const trpcClient = trpc.useUtils().client;
  const utils = trpc.useUtils();
  const { mutateAsync: deleteWorkspaceLayouts } =
    trpc.workspaceLayouts.deleteWorkspaceLayouts.useMutation();
  const { mutateAsync: setWorkspaceLayoutAsDefault } =
    trpc.workspaceLayouts.setWorkspaceLayoutAsDefault.useMutation();
  const { mutateAsync: clearWorkspaceLayoutDefault } =
    trpc.workspaceLayouts.clearWorkspaceLayoutDefault.useMutation();
  const { mutateAsync: createSharedResource } =
    trpc.sharedResources.createSharedResource.useMutation();
  const { mutateAsync: importSharedResource } =
    trpc.sharedResources.importSharedResource.useMutation();
  const { mutateAsync: updateSharedResource } =
    trpc.sharedResources.updateSharedResource.useMutation();
  const { mutateAsync: revokeSharedResource } =
    trpc.sharedResources.revokeSharedResource.useMutation();
  const { onUpdateSubmit } = useCreateWorkspaceLayout();
  const chartState = useObservable(() => getState$(), new Map());

  const handleDelete = useCallback(
    async (layoutIds: string[]) => {
      try {
        const count = await deleteWorkspaceLayouts({ layoutIds });
        await utils.workspaceLayouts.workspaceLayouts.invalidate();

        notify({
          type: 'success',
          message:
            count > 0
              ? `${count} workspace layout${count > 1 ? 's' : ''} deleted successfully`
              : 'No workspace layouts were deleted'
        });
      } catch (error) {
        notify({
          type: 'error',
          message:
            error instanceof Error
              ? error.message
              : 'Failed to delete workspace layouts'
        });
      }
    },
    [deleteWorkspaceLayouts, notify, utils]
  );

  const handleSetDefault = useCallback(
    async (layoutId: string, isDefault: boolean) => {
      try {
        if (isDefault) {
          await clearWorkspaceLayoutDefault({});
        } else {
          await setWorkspaceLayoutAsDefault({ layoutId });
        }
        await utils.workspaceLayouts.workspaceLayouts.invalidate();

        notify({
          type: 'success',
          message: isDefault
            ? 'Workspace layout unmarked as default'
            : 'Default workspace layout updated'
        });
      } catch (error) {
        notify({
          type: 'error',
          message:
            error instanceof Error
              ? error.message
              : 'Failed to set default workspace layout'
        });
      }
    },
    [clearWorkspaceLayoutDefault, notify, setWorkspaceLayoutAsDefault, utils]
  );

  const handleImportShared = useCallback(
    async (sharedResourceId: string, sharedResourceName: string) => {
      try {
        await importSharedResource({
          sharedResourceId,
          name: sharedResourceName
        });

        await Promise.all([
          utils.workspaceLayouts.workspaceLayouts.invalidate(),
          utils.sharedResources.listSharedResources.invalidate()
        ]);

        notify({
          type: 'success',
          message: 'Shared workspace layout imported'
        });
      } catch (error) {
        notify({
          type: 'error',
          message:
            error instanceof Error
              ? error.message
              : 'Failed to import shared workspace layout'
        });
      }
    },
    [importSharedResource, notify, utils]
  );

  const handleShare = useCallback(
    async (layoutId: string) => {
      try {
        await createSharedResource({
          resourceType: 'workspaceLayout',
          layoutId
        });

        await utils.sharedResources.listSharedResources.invalidate();

        notify({
          type: 'success',
          message: 'Workspace layout published to Shared'
        });
      } catch (error) {
        notify({
          type: 'error',
          message:
            error instanceof Error
              ? error.message
              : 'Failed to share workspace layout'
        });
      }
    },
    [createSharedResource, notify, utils]
  );

  const handleModifyShared = useCallback(
    async (
      sharedResourceId: string,
      sharedResourceName: string,
      sharedResourceNotes?: string
    ) => {
      try {
        const sharedResource =
          await trpcClient.sharedResources.getSharedResource.query({
            sharedResourceId
          });

        const fallbackSnapshot =
          sharedResource.payload.resourceType === 'workspaceLayout'
            ? sharedResource.payload.snapshot
            : null;

        const snapshot =
          chartState.size > 0
            ? buildWorkspaceLayoutSnapshot(chartState)
            : fallbackSnapshot;

        if (!snapshot) {
          notify({
            type: 'error',
            message: 'No workspace layout state found'
          });
          return;
        }

        openWindow({
          type: 'SaveTradingLayoutForm',
          sizePreset: 'form',
          componentProps: {
            onSubmit: async ({ name, notes }) => {
              try {
                await updateSharedResource({
                  resourceType: 'workspaceLayout',
                  sharedResourceId,
                  name,
                  notes: notes?.trim() || undefined,
                  snapshot
                });

                await utils.sharedResources.listSharedResources.invalidate();

                notify({
                  type: 'success',
                  message: 'Shared workspace layout updated successfully'
                });

                closeWindow('SaveTradingLayoutForm');
              } catch (error) {
                notify({
                  type: 'error',
                  message:
                    error instanceof Error
                      ? error.message
                      : 'Failed to update shared workspace layout'
                });
              }
            },
            initialValues: {
              name: sharedResourceName,
              notes:
                sharedResource.payload.resourceType === 'workspaceLayout'
                  ? sharedResource.payload.notes ?? sharedResourceNotes ?? ''
                  : sharedResourceNotes
            },
            submitLabel: 'Update Shared'
          }
        });
      } catch (error) {
        notify({
          type: 'error',
          message:
            error instanceof Error
              ? error.message
              : 'Failed to prepare shared workspace layout update'
        });
      }
    },
    [
      chartState,
      closeWindow,
      notify,
      openWindow,
      trpcClient,
      updateSharedResource,
      utils.sharedResources.listSharedResources
    ]
  );

  const handleRevokeShared = useCallback(
    async (sharedResourceId: string) => {
      try {
        await revokeSharedResource({ sharedResourceId });
        await utils.sharedResources.listSharedResources.invalidate();

        notify({
          type: 'success',
          message: 'Shared workspace layout revoked'
        });
      } catch (error) {
        notify({
          type: 'error',
          message:
            error instanceof Error
              ? error.message
              : 'Failed to revoke shared workspace layout'
        });
      }
    },
    [notify, revokeSharedResource, utils]
  );

  useEffect(() => {
    const subscription = openWidget$()
      .pipe(
        filterWidgetEvent<WidgetPayload, 'ManageWorkspaceLayouts'>(
          'ManageWorkspaceLayouts'
        ),
        takeUntil(workspaceDestroy$)
      )
      .subscribe({
        next: ({ activeLayoutId, onApplied }) => {
          const handleApply = async (layoutId: string) => {
            try {
              const layouts =
                await trpcClient.workspaceLayouts.workspaceLayouts.query();
              const layout = layouts.find(({ id }) => id === layoutId);

              if (!layout) {
                throw new Error('Workspace layout not found');
              }

              const resolvedFormStates = await resolveLayoutStatesToFormState(
                trpcClient,
                layout.snapshot.charts.map(
                  ({ layout: chartLayout }) => chartLayout.formState
                )
              );

              dispatch({ type: 'CLEAR_STATE' });
              layout.snapshot.charts.forEach(
                ({ chartId, layout: chartLayout }, index) => {
                  dispatch({
                    type: 'ADD_CHART',
                    payload: {
                      id: chartId,
                      formState: resolvedFormStates[index]
                    }
                  });
                }
              );

              notify({
                type: 'success',
                message: 'Workspace layout applied'
              });

              onApplied?.(layout.id);
              closeWindow('ManageWorkspaceLayouts');
            } catch (error) {
              notify({
                type: 'error',
                message:
                  error instanceof Error
                    ? error.message
                    : 'Failed to apply workspace layout'
              });
            }
          };

          const handleModify = async (
            layoutId: string,
            layoutName: string,
            layoutNotes?: string
          ) => {
            try {
              const layouts =
                await trpcClient.workspaceLayouts.workspaceLayouts.query();
              const selectedLayout = layouts.find(({ id }) => id === layoutId);

              if (!selectedLayout) {
                throw new Error('Workspace layout not found');
              }

              // Use current workspace snapshot when available so "Modify" actually
              // updates layout content, not only the name.
              const snapshot =
                chartState.size > 0
                  ? buildWorkspaceLayoutSnapshot(chartState)
                  : selectedLayout.snapshot;

              openWindow({
                type: 'SaveTradingLayoutForm',
                sizePreset: 'form',
                componentProps: {
                  onSubmit: onUpdateSubmit(layoutId, snapshot),
                  initialValues: { name: layoutName, notes: layoutNotes },
                  submitLabel: 'Update'
                }
              });
            } catch (error) {
              notify({
                type: 'error',
                message:
                  error instanceof Error
                    ? error.message
                    : 'Failed to prepare workspace layout update'
              });
            }
          };

          openWindow({
            type: 'ManageWorkspaceLayouts',
            sizePreset: 'grid',
            componentProps: {
              activeLayoutId,
              onApply: handleApply,
              onDelete: handleDelete,
              onModify: handleModify,
              onImportShared: handleImportShared,
              onModifyShared: handleModifyShared,
              onRevokeShared: handleRevokeShared,
              onSetDefault: handleSetDefault,
              onShare: (layoutId) => handleShare(layoutId)
            }
          });
        },
        error: (err) =>
          console.error('Error in ManageWorkspaceLayouts subscription:', err)
      });

    return () => subscription.unsubscribe();
  }, [
    chartState,
    closeWindow,
    createSharedResource,
    dispatch,
    handleDelete,
    handleShare,
    handleImportShared,
    handleModifyShared,
    handleRevokeShared,
    handleSetDefault,
    notify,
    getState$,
    onUpdateSubmit,
    openWidget$,
    openWindow,
    trpcClient,
    workspaceDestroy$
  ]);
};
